import { hash32, hashString } from './rng';

const ADJ = [
  'AMBER', 'BRAVE', 'BREEZY', 'CLOUDY', 'COSMIC', 'DAWN', 'DUSTY', 'EMBER', 'FABLED', 'FLEET', 'GENTLE', 'GILDED',
  'GOLDEN', 'HAZY', 'HOLLOW', 'IVORY', 'JADE', 'LUCKY', 'LUNAR', 'MELLOW', 'MISTY', 'MOSSY', 'NIMBLE', 'NOBLE',
  'OCHRE', 'PAPER', 'QUIET', 'QUICK', 'ROSY', 'RUSTY', 'SAFFRON', 'SILVER', 'SLEEPY', 'SOLAR', 'STORMY', 'SUNNY',
  'SWIFT', 'TAWNY', 'TIDAL', 'VELVET', 'WANDER', 'WILD', 'WINDY', 'WISPY', 'ZEPHYR', 'COPPER', 'CORAL', 'DAPPLED',
  'DREAMY', 'FROSTY', 'GLASSY', 'HUSHED', 'KINDLED', 'LOFTY', 'MAPLE', 'MERRY', 'PEARL', 'PRISM', 'RADIANT', 'SANDY',
  'SCARLET', 'SOARING', 'TUMBLE', 'VIVID',
] as const;

const NOUN = [
  'ACORN', 'ARCH', 'BADGER', 'BEACON', 'BRAMBLE', 'BREEZE', 'CACTUS', 'CANYON', 'CLOVER', 'COMET', 'CRANE', 'DUNE',
  'EAGLE', 'FALCON', 'FEATHER', 'FERN', 'FINCH', 'FOX', 'GALE', 'GECKO', 'HARE', 'HAWK', 'HERON', 'ISLE', 'KESTREL',
  'KITE', 'LANTERN', 'LARK', 'MEADOW', 'MESA', 'MOTH', 'OASIS', 'OTTER', 'PEBBLE', 'PINE', 'PLUME', 'POPPY', 'QUAIL',
  'RAVEN', 'RIDGE', 'SPARROW', 'SPIRE', 'SQUALL', 'STAR', 'THISTLE', 'THRUSH', 'VALE', 'WILLOW', 'WREN', 'ZEPHYR',
  'BLOSSOM', 'CINDER', 'DRIFT', 'EMBER', 'GLADE', 'HOLLOW', 'IBIS', 'JUNIPER', 'LOTUS', 'MARIGOLD', 'NOMAD', 'ORCHID',
  'PRAIRIE', 'SAGE',
] as const;

/** Canonical form of any user-typed seed code: upper-case, words separated by single dashes. */
export function normalizeSeedCode(code: string): string {
  return code
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Turns any seed code into the 32-bit world seed. Scoped by world version so old codes never silently change meaning. */
export function seedFromCode(code: string, worldVersion: number): number {
  return hash32(hashString(normalizeSeedCode(code) || 'HARE'), worldVersion);
}

/** Friendly code from a 32-bit value, e.g. "AMBER-FOX-42". */
export function codeFromNumber(n: number): string {
  const h = hash32(n, 0xc0de);
  const a = ADJ[h % ADJ.length]!;
  const b = NOUN[(h >>> 8) % NOUN.length]!;
  const num = (h >>> 20) % 100;
  return `${a}-${b}-${num}`;
}

/** Random-looking code. Takes entropy from the caller (UI layer may use Math.random — the code itself is the seed). */
export function randomSeedCode(entropy: number): string {
  return codeFromNumber(Math.floor(entropy * 4294967296) >>> 0);
}

/** Everyone in the world gets the same daily code (UTC date). */
export function dailySeedCode(date: Date): string {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  return codeFromNumber(hashString(`daily-${y}-${m}-${d}`));
}

export function isoDay(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
